import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from './AuthContext';
import { setUserType } from '../../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDefaultRouteForUser } from '../../utils/onboardingStatus';
import './RoleSelection.scss';

const RoleSelection = () => {
    const { t } = useTranslation('auth');
    const { currentUser, refreshUser } = useAuth();
    const [selectedRole, setSelectedRole] = useState(currentUser?.user_type === 'none' ? null : currentUser?.user_type);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [completed, setCompleted] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Verified accounts cannot reopen the role correction flow.
    React.useEffect(() => {
        if (!isSubmitting && !completed && currentUser && currentUser.user_type && currentUser.user_type !== 'none' && !currentUser.can_change_user_type) {
            if (currentUser.user_type === 'seller') {
                navigate('/seller/dashboard', { replace: true });
                return;
            }

            navigate(getDefaultRouteForUser(currentUser), { replace: true });
        }
    }, [currentUser, navigate, isSubmitting, completed]);

    const handleConfirm = async () => {
        if (!selectedRole || isSubmitting) return;
        
        setError('');
        setIsSubmitting(true);
        try {
            await setUserType(selectedRole);
            await refreshUser();
            
            const changingRole = currentUser?.user_type !== 'none' && currentUser?.user_type !== selectedRole;
            if (changingRole) sessionStorage.removeItem('foth_invite_return_to');
            setCompleted(true);
            navigate('/complete-profile', { state: { role: selectedRole, returnTo: changingRole ? undefined : location.state?.returnTo } });
        } catch (error) {
            console.error('Error setting user type:', error);
            setError(error.response?.data?.error || t('role_selection.error'));
            await refreshUser();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="role-selection-page">
            <motion.div 
                className="role-selection-container"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
            >
                <div className="role-header">
                    <motion.h1
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.6 }}
                    >
                        {t('role_selection.title')}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                    >
                        {t('role_selection.subtitle')}
                    </motion.p>
                </div>

                <div className="roles-grid">
                    {/* Creator Option */}
                    <motion.button
                        type="button"
                        aria-pressed={selectedRole === 'creator'}
                        disabled={isSubmitting}
                        className={`role-card ${selectedRole === 'creator' ? 'active' : ''}`}
                        onClick={() => setSelectedRole('creator')}
                    >
                        <div className="role-icon-wrap">
                            <Sparkles size={28} strokeWidth={1.5} />
                        </div>
                        <div className="role-text-content">
                            <h3>{t('role_selection.creator.title')}</h3>
                            <p>{t('role_selection.creator.description')}</p>
                        </div>
                        <div className="role-check">
                            <CheckCircle2 size={24} strokeWidth={2} />
                        </div>
                    </motion.button>

                    {/* Business Option */}
                    <motion.button
                        type="button"
                        aria-pressed={selectedRole === 'business'}
                        disabled={isSubmitting}
                        className={`role-card ${selectedRole === 'business' ? 'active' : ''}`}
                        onClick={() => setSelectedRole('business')}
                    >
                        <div className="role-icon-wrap gold">
                            <Building2 size={28} strokeWidth={1.5} />
                        </div>
                        <div className="role-text-content">
                            <h3>{t('role_selection.business.title')}</h3>
                            <p>{t('role_selection.business.description')}</p>
                        </div>
                        <div className="role-check">
                            <CheckCircle2 size={24} strokeWidth={2} />
                        </div>
                    </motion.button>
                </div>

                <p className="role-selection-note">{t('role_selection.change_note')}</p>
                {!selectedRole && <p className="role-selection-note">{t('role_selection.select_hint')}</p>}
                {currentUser?.user_type !== 'none' && selectedRole && selectedRole !== currentUser?.user_type && (
                    <p className="role-selection-note">{t('role_selection.change_warning')}</p>
                )}
                {error && <p className="role-selection-error" role="alert">{error}</p>}

                <div className="role-footer">
                    <motion.button
                        className="confirm-btn"
                        disabled={!selectedRole || isSubmitting}
                        onClick={handleConfirm}
                        whileHover={!selectedRole || isSubmitting ? {} : { scale: 1.02 }}
                        whileTap={!selectedRole || isSubmitting ? {} : { scale: 0.98 }}
                    >
                        {isSubmitting ? (
                            <span className="spinner" />
                        ) : (
                            <>{selectedRole ? t(`role_selection.continue_${selectedRole}`) : t('role_selection.submit')} <ArrowRight size={20} /></>
                        )}
                    </motion.button>
                </div>
            </motion.div>
        </div>
    );
};

export default RoleSelection;
